import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { getUsuarioSessao } from '../utils/sessao';
import { navegarComEnter } from '../utils/enterNavegacao';
import s from './Page.module.css';
import sf from './TiposMovimentoFiscal.module.css';

const TIPO_OP = [
  { v: 'S', l: 'Saída' },
  { v: 'E', l: 'Entrada' },
];

// CST (regime normal) é 2 dígitos ("00".."90"), não 3 — mesmo achado/
// correção de CadastroProdutos.jsx#CSTS_ICMS (2026-09-16): valores errados
// aqui gravariam uma conversão que nunca bate com nenhum `case` de
// services/sefaz/icms.js#montarIcmsNormal e quebraria a nota. CSOSN
// (Simples Nacional) é 3 dígitos mesmo, esses continuam certos.
const CSTS_ICMS = [
  '00','10','20','30','40','41','50','51','60','70','90',
  '101','102','103','201','202','203','300','400','500','900',
];
const CSTS_PIS_COFINS = [
  '01','02','03','04','05','06','07','08','09','49',
  '50','51','52','53','54','55','56','60','61','62','63','64','65','66',
  '70','71','72','73','74','75','98','99',
];

// Modalidade de BC do ICMS (regime normal) — mesmas opções de
// CadastroProdutos.jsx, usadas na Config. Fiscal "Fora do Estado" abaixo.
const MODS_BC_ICMS = [
  { v: '0', l: '0 — Margem Valor Agregado (%)' },
  { v: '1', l: '1 — Pauta (Valor)' },
  { v: '2', l: '2 — Preço Tabelado Máximo (valor)' },
  { v: '3', l: '3 — Valor da Operação' },
];

// Config. Fiscal "Fora do Estado" (regime normal, só tipos de Saída) —
// ICMS/PIS/COFINS de tipo_movimento_tributos_fora_estado (migration 180).
// Campo vazio ('') = bloco não sobrescrito, sai como NULL no PUT — mesmo
// padrão de "nulo = usa o produto" do resto do sistema.
const VAZIO_FORA_ESTADO = {
  icms_cst: '', icms_mod_bc: '', icms_red_bc_perc: '', icms_aliq_perc: '', icms_dif_perc: '',
  icms_st_mod_bc: '', icms_st_red_bc_perc: '', icms_st_mva_perc: '', icms_st_aliq_perc: '',
  icms_st_ret_bc: '', icms_st_ret_valor: '',
  pis_cst: '', pis_aliq_perc: '', cofins_cst: '', cofins_aliq_perc: '',
};

const VAZIO = {
  nome: '',
  tipo_operacao: 'S',
  cfop_dentro_estado_id: '',
  cfop_fora_estado_id: '',
  cfop_st_dentro_estado_id: '',
  cfop_st_fora_estado_id: '',
  cfop_producao_dentro_estado_id: '',
  cfop_producao_fora_estado_id: '',
  natureza_operacao: '',
  gera_financeiro:   true,
  movimenta_estoque: true,
  usa_preco_custo:   false,
  observacao: '',
  setor_estoque_id: '',
  solicitar_setor: false,
  mostrar_em_pedidos: true,
  verificar_estoque_insuficiente: false,
  // Índices p/ Coeficiente de Preço (Simulação de Preço)
  idx_icms_pct: '0', idx_csll_pct: '0', idx_irpj_pct: '0',
  idx_custo_op_pct: '0', idx_outros_pct: '0', idx_simples_pct: '0', idx_comissao_pct: '0',
};

// Campos do print "Índices Para Coeficiente de Preço e Compras"
const IDX_CAMPOS = [
  { k: 'idx_icms_pct',     label: '%ICMS D/E',   regime: 'normal'  },
  { k: 'idx_csll_pct',     label: '%C.S.L.L',    regime: 'normal'  },
  { k: 'idx_irpj_pct',     label: '%I.R.P.J',    regime: 'normal'  },
  { k: 'idx_custo_op_pct', label: '%Custo OP',   regime: 'ambos'   },
  { k: 'idx_outros_pct',   label: '%Outros',     regime: 'ambos'   },
  { k: 'idx_comissao_pct', label: '%Comissão',   regime: 'ambos'   },
  { k: 'idx_simples_pct',  label: '%Simples Nac', regime: 'simples' },
];

/* ── Mini-tabela de conversão genérica ── */
function TabelaConversao({ titulo, hint, colunaXml, colunaEntrada, colunaSaida, itens, onAdd, onRemove, optsXml, optsEntrada, optsSaida }) {
  const [linha, setLinha] = useState({ xml: '', entrada: '', saida: '' });
  const [erro, setErro]   = useState('');
  const entRef = useRef(null);
  const saiRef = useRef(null);

  const handleAdd = () => {
    if (!linha.xml.trim() || !linha.entrada.trim() || !linha.saida.trim()) {
      setErro('Preencha os três campos.'); return;
    }
    setErro('');
    onAdd(linha.xml.trim(), linha.entrada.trim(), linha.saida.trim());
    setLinha({ xml: '', entrada: '', saida: '' });
  };

  return (
    <div className={sf.convCard}>
      <div className={sf.convTitulo}>{titulo}</div>
      {hint && <div className={sf.convHint}>{hint}</div>}

      {itens.length > 0 && (
        <table className={sf.convTabela}>
          <thead>
            <tr>
              <th>{colunaXml}</th>
              <th style={{ width: 16, textAlign: 'center' }}>→</th>
              <th>{colunaEntrada}</th>
              <th>{colunaSaida}</th>
              <th style={{ width: 24 }}></th>
            </tr>
          </thead>
          <tbody>
            {itens.map(it => (
              <tr key={it.cst_xml || it.cfop_xml}>
                <td><span className={sf.convCodXml}>{it.cst_xml || it.cfop_xml}</span></td>
                <td style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 11 }}>→</td>
                <td><span className={sf.convCodEnt}>{it.cst_entrada || it.cfop_entrada}</span></td>
                <td><span className={sf.convCodSai}>{it.cst_saida}</span></td>
                <td>
                  <button type="button" className={sf.convBtnRem}
                    onClick={() => onRemove(it.cst_xml || it.cfop_xml)}>✕</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {itens.length === 0 && (
        <div className={sf.convVazio}>Nenhuma conversão. Adicione abaixo.</div>
      )}

      <div className={sf.convAddRow}>
        <div className={sf.convAddField}>
          <label className={sf.convAddLabel}>{colunaXml} (XML)</label>
          <input className={sf.convInput}
            list={`opts-xml-${titulo}`}
            placeholder="ex: 400"
            value={linha.xml}
            onChange={e => setLinha(v => ({ ...v, xml: e.target.value }))}
            onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), entRef.current?.focus())}
          />
          <datalist id={`opts-xml-${titulo}`}>
            {optsXml.map(o => <option key={o} value={o} />)}
          </datalist>
        </div>
        <span className={sf.convSeta}>→</span>
        <div className={sf.convAddField}>
          <label className={sf.convAddLabel}>{colunaEntrada}</label>
          <input className={sf.convInput}
            ref={entRef}
            list={`opts-ent-${titulo}`}
            placeholder="ex: 102"
            value={linha.entrada}
            onChange={e => setLinha(v => ({ ...v, entrada: e.target.value }))}
            onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), saiRef.current?.focus())}
          />
          <datalist id={`opts-ent-${titulo}`}>
            {optsEntrada.map(o => <option key={o} value={o} />)}
          </datalist>
        </div>
        <div className={sf.convAddField}>
          <label className={sf.convAddLabel}>{colunaSaida}</label>
          <input className={sf.convInput}
            ref={saiRef}
            list={`opts-sai-${titulo}`}
            placeholder="ex: 400"
            value={linha.saida}
            onChange={e => setLinha(v => ({ ...v, saida: e.target.value }))}
            onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAdd())}
          />
          <datalist id={`opts-sai-${titulo}`}>
            {optsSaida.map(o => <option key={o} value={o} />)}
          </datalist>
        </div>
        <button type="button" className={sf.convBtnAdd} onClick={handleAdd}>+ Add</button>
      </div>
      {erro && <p className={sf.convErro}>{erro}</p>}
    </div>
  );
}

/* ── Componente de conversão de CFOP (diferente pois não tem coluna saída) ── */
function TabelaCfopConversao({ itens, onAdd, onRemove, cfopsXml, cfopsEntrada }) {
  const [linha, setLinha] = useState({ xml: '', entrada: '' });
  const [erro, setErro]   = useState('');
  const entRef = useRef(null);

  const handleAdd = () => {
    if (!linha.xml.trim() || !linha.entrada.trim()) { setErro('Preencha os dois CFOPs.'); return; }
    if (!/^\d{4}$/.test(linha.xml.trim()))    { setErro('CFOP XML deve ter 4 dígitos.'); return; }
    if (!/^\d{4}$/.test(linha.entrada.trim())) { setErro('CFOP Entrada deve ter 4 dígitos.'); return; }
    setErro('');
    onAdd(linha.xml.trim(), linha.entrada.trim());
    setLinha({ xml: '', entrada: '' });
  };

  return (
    <div className={sf.convCard}>
      <div className={sf.convTitulo}>Conversão de CFOP</div>
      <div className={sf.convHint}>CFOP que vem no XML do fornecedor → CFOP de entrada real</div>

      {itens.length > 0 && (
        <table className={sf.convTabela}>
          <thead>
            <tr>
              <th>CFOP XML</th>
              <th style={{ width: 16, textAlign: 'center' }}>→</th>
              <th>CFOP Entrada</th>
              <th style={{ width: 24 }}></th>
            </tr>
          </thead>
          <tbody>
            {itens.map(it => (
              <tr key={it.cfop_xml}>
                <td><span className={sf.convCodXml}>{it.cfop_xml}</span></td>
                <td style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 11 }}>→</td>
                <td><span className={sf.convCodEnt}>{it.cfop_entrada}</span></td>
                <td>
                  <button type="button" className={sf.convBtnRem}
                    onClick={() => onRemove(it.cfop_xml)}>✕</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {itens.length === 0 && <div className={sf.convVazio}>Nenhuma conversão.</div>}

      <div className={sf.convAddRow}>
        <div className={sf.convAddField}>
          <label className={sf.convAddLabel}>CFOP no XML</label>
          <input className={sf.convInput}
            list="cfop-xml-opts"
            placeholder="ex: 5102"
            maxLength={4}
            value={linha.xml}
            onChange={e => setLinha(v => ({ ...v, xml: e.target.value.replace(/\D/g,'') }))}
            onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), entRef.current?.focus())}
          />
          <datalist id="cfop-xml-opts">
            {cfopsXml.map(c => <option key={c.codigo} value={c.codigo}>{c.codigo} — {c.descricao}</option>)}
          </datalist>
        </div>
        <span className={sf.convSeta}>→</span>
        <div className={sf.convAddField}>
          <label className={sf.convAddLabel}>CFOP Entrada</label>
          <input className={sf.convInput}
            ref={entRef}
            list="cfop-ent-opts"
            placeholder="ex: 1102"
            maxLength={4}
            value={linha.entrada}
            onChange={e => setLinha(v => ({ ...v, entrada: e.target.value.replace(/\D/g,'') }))}
            onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAdd())}
          />
          <datalist id="cfop-ent-opts">
            {cfopsEntrada.map(c => <option key={c.codigo} value={c.codigo}>{c.codigo} — {c.descricao}</option>)}
          </datalist>
        </div>
        <button type="button" className={sf.convBtnAdd} onClick={handleAdd}>+ Add</button>
      </div>
      {erro && <p className={sf.convErro}>{erro}</p>}
    </div>
  );
}

/* ══════════════════════════════════
   Componente Principal
══════════════════════════════════ */
export default function TiposMovimentoFiscal() {
  const [lista, setLista]     = useState([]);
  const [cfops, setCfops]     = useState([]);
  const [setores, setSetores] = useState([]);
  const [form, setForm]       = useState(VAZIO);
  const [editId, setEditId]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro]       = useState('');
  const [ok, setOk]           = useState('');
  const [mostrarForm, setMostrarForm] = useState(false);

  // Conversões
  const [convCfop,       setConvCfop]       = useState([]);
  const [convCstIcms,    setConvCstIcms]    = useState([]);
  const [convPisCofins,  setConvPisCofins]  = useState([]);

  // Config. Fiscal "Fora do Estado" (ICMS/PIS/COFINS, só tipos de Saída)
  const [formForaEstado, setFormForaEstado] = useState(VAZIO_FORA_ESTADO);
  const [erroForaEstado, setErroForaEstado] = useState('');
  const [okForaEstado,   setOkForaEstado]   = useState('');
  const [salvandoForaEstado, setSalvandoForaEstado] = useState(false);

  // Regime tributário da organização define quais índices de preço valem
  const [orgData, setOrgData] = useState(null);
  useEffect(() => {
    const u = getUsuarioSessao();
    if (u?.organizacao_id)
      api.get(`/organizacoes/${u.organizacao_id}`).then(r => setOrgData(r.data)).catch(() => {});
  }, []);
  const ehSimples = /simples|mei/i.test(orgData?.regime_tributario || 'Simples Nacional');

  const carregar = useCallback(async () => {
    try {
      const [tmf, seto] = await Promise.all([
        api.get('/fiscal/tipos-movimento'),
        api.get('/setores-estoque'),
      ]);
      setLista(tmf.data);
      setSetores(seto.data);
    } catch { setErro('Erro ao carregar tipos.'); }
  }, []);

  const carregarCfops = useCallback(async (tipo) => {
    try {
      const { data } = await api.get('/fiscal/cfop', { params: { tipo } });
      setCfops(data);
    } catch { setCfops([]); }
  }, []);

  const carregarConversoes = useCallback(async (id) => {
    if (!id) { setConvCfop([]); setConvCstIcms([]); setConvPisCofins([]); setFormForaEstado(VAZIO_FORA_ESTADO); return; }
    try {
      const [cfopR, icmsR, pisR, foraR] = await Promise.all([
        api.get(`/fiscal/tipos-movimento/${id}/conversoes-cfop`),
        api.get(`/fiscal/tipos-movimento/${id}/conversoes-cst-icms`),
        api.get(`/fiscal/tipos-movimento/${id}/conversoes-cst-pis-cofins`),
        api.get(`/fiscal/tipos-movimento/${id}/tributos-fora-estado`),
      ]);
      setConvCfop(Array.isArray(cfopR.data) ? cfopR.data : []);
      setConvCstIcms(Array.isArray(icmsR.data) ? icmsR.data : []);
      setConvPisCofins(Array.isArray(pisR.data) ? pisR.data : []);
      const fe = foraR.data;
      setFormForaEstado(fe ? {
        icms_cst: fe.icms_cst || '', icms_mod_bc: fe.icms_mod_bc ?? '',
        icms_red_bc_perc: fe.icms_red_bc_perc ?? '', icms_aliq_perc: fe.icms_aliq_perc ?? '',
        icms_dif_perc: fe.icms_dif_perc ?? '',
        icms_st_mod_bc: fe.icms_st_mod_bc ?? '', icms_st_red_bc_perc: fe.icms_st_red_bc_perc ?? '',
        icms_st_mva_perc: fe.icms_st_mva_perc ?? '', icms_st_aliq_perc: fe.icms_st_aliq_perc ?? '',
        icms_st_ret_bc: fe.icms_st_ret_bc ?? '', icms_st_ret_valor: fe.icms_st_ret_valor ?? '',
        pis_cst: fe.pis_cst || '', pis_aliq_perc: fe.pis_aliq_perc ?? '',
        cofins_cst: fe.cofins_cst || '', cofins_aliq_perc: fe.cofins_aliq_perc ?? '',
      } : VAZIO_FORA_ESTADO);
      setErroForaEstado(''); setOkForaEstado('');
    } catch { setConvCfop([]); setConvCstIcms([]); setConvPisCofins([]); setFormForaEstado(VAZIO_FORA_ESTADO); }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => { carregarCfops(form.tipo_operacao); }, [form.tipo_operacao, carregarCfops]);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  function novo() {
    setEditId(null);
    setForm(VAZIO);
    setErro(''); setOk('');
    setConvCfop([]); setConvCstIcms([]); setConvPisCofins([]);
    setFormForaEstado(VAZIO_FORA_ESTADO); setErroForaEstado(''); setOkForaEstado('');
    setMostrarForm(true);
  }

  function editar(item) {
    setEditId(item.id);
    setForm({
      nome:                     item.nome,
      tipo_operacao:            item.tipo_operacao,
      cfop_dentro_estado_id:    item.cfop_dentro_estado_id    || '',
      cfop_fora_estado_id:      item.cfop_fora_estado_id      || '',
      cfop_st_dentro_estado_id: item.cfop_st_dentro_estado_id || '',
      cfop_st_fora_estado_id:   item.cfop_st_fora_estado_id   || '',
      cfop_producao_dentro_estado_id: item.cfop_producao_dentro_estado_id || '',
      cfop_producao_fora_estado_id:   item.cfop_producao_fora_estado_id   || '',
      natureza_operacao: item.natureza_operacao || '',
      gera_financeiro:   item.gera_financeiro   ?? true,
      movimenta_estoque: item.movimenta_estoque ?? true,
      usa_preco_custo:   item.usa_preco_custo   ?? false,
      observacao: item.observacao || '',
      setor_estoque_id: item.setor_estoque_id || '',
      solicitar_setor: item.solicitar_setor ?? false,
      mostrar_em_pedidos: item.mostrar_em_pedidos ?? true,
      verificar_estoque_insuficiente: item.verificar_estoque_insuficiente ?? false,
      idx_icms_pct:     String(item.idx_icms_pct     ?? 0),
      idx_csll_pct:     String(item.idx_csll_pct     ?? 0),
      idx_irpj_pct:     String(item.idx_irpj_pct     ?? 0),
      idx_custo_op_pct: String(item.idx_custo_op_pct ?? 0),
      idx_outros_pct:   String(item.idx_outros_pct   ?? 0),
      idx_simples_pct:  String(item.idx_simples_pct  ?? 0),
      idx_comissao_pct: String(item.idx_comissao_pct ?? 0),
    });
    setErro(''); setOk('');
    carregarConversoes(item.id);
    setMostrarForm(true);
  }

  function fecharForm() {
    setMostrarForm(false);
    setEditId(null);
    setForm(VAZIO);
    setErro(''); setOk('');
  }

  async function salvar(e) {
    e.preventDefault();
    if (!form.nome.trim()) { setErro('Informe o nome.'); return; }
    if (!form.cfop_dentro_estado_id) { setErro('Informe o CFOP Dentro do Estado.'); return; }
    if (!form.cfop_fora_estado_id)   { setErro('Informe o CFOP Fora do Estado.'); return; }
    const payload = {
      ...form,
      cfop_st_dentro_estado_id: form.cfop_st_dentro_estado_id || null,
      cfop_st_fora_estado_id:   form.cfop_st_fora_estado_id   || null,
      cfop_producao_dentro_estado_id: form.cfop_producao_dentro_estado_id || null,
      cfop_producao_fora_estado_id:   form.cfop_producao_fora_estado_id   || null,
      observacao: form.observacao || null,
      setor_estoque_id: form.setor_estoque_id || null,
    };
    setLoading(true); setErro(''); setOk('');
    try {
      if (editId) {
        await api.put(`/fiscal/tipos-movimento/${editId}`, payload);
      } else {
        const { data } = await api.post('/fiscal/tipos-movimento', payload);
        setEditId(data.id);
        carregarConversoes(data.id);
      }
      setOk('Salvo com sucesso!');
      carregar();
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao salvar.');
    } finally { setLoading(false); }
  }

  async function excluir(item) {
    if (!window.confirm(`Excluir "${item.nome}"?`)) return;
    try {
      await api.delete(`/fiscal/tipos-movimento/${item.id}`);
      carregar();
    } catch (err) {
      alert(err.response?.data?.erro || 'Erro ao excluir.');
    }
  }

  // Handlers de conversão CFOP
  const addConvCfop = async (xml, ent) => {
    await api.post(`/fiscal/tipos-movimento/${editId}/conversoes-cfop`, { cfop_xml: xml, cfop_entrada: ent });
    carregarConversoes(editId);
  };
  const remConvCfop = async (xml) => {
    await api.delete(`/fiscal/tipos-movimento/${editId}/conversoes-cfop/${xml}`);
    carregarConversoes(editId);
  };

  // Handlers de conversão CST ICMS
  const addConvCstIcms = async (xml, ent, sai) => {
    await api.post(`/fiscal/tipos-movimento/${editId}/conversoes-cst-icms`, { cst_xml: xml, cst_entrada: ent, cst_saida: sai });
    carregarConversoes(editId);
  };
  const remConvCstIcms = async (xml) => {
    await api.delete(`/fiscal/tipos-movimento/${editId}/conversoes-cst-icms/${xml}`);
    carregarConversoes(editId);
  };

  // Handlers de conversão CST PIS/COFINS
  const addConvPisCofins = async (xml, ent, sai) => {
    await api.post(`/fiscal/tipos-movimento/${editId}/conversoes-cst-pis-cofins`, { cst_xml: xml, cst_entrada: ent, cst_saida: sai });
    carregarConversoes(editId);
  };
  const remConvPisCofins = async (xml) => {
    await api.delete(`/fiscal/tipos-movimento/${editId}/conversoes-cst-pis-cofins/${xml}`);
    carregarConversoes(editId);
  };

  function setForaEstado(k, v) { setFormForaEstado(f => ({ ...f, [k]: v })); }

  const salvarForaEstado = async () => {
    setSalvandoForaEstado(true); setErroForaEstado(''); setOkForaEstado('');
    try {
      await api.put(`/fiscal/tipos-movimento/${editId}/tributos-fora-estado`, formForaEstado);
      setOkForaEstado('Salvo com sucesso!');
    } catch (err) {
      setErroForaEstado(err.response?.data?.erro || 'Erro ao salvar.');
    } finally { setSalvandoForaEstado(false); }
  };

  // CFOPs filtrados
  const cfopsDentro = cfops.filter(c => form.tipo_operacao === 'S' ? c.codigo[0]==='5' && !['5401','5402','5403','5405'].includes(c.codigo) : c.codigo[0]==='1');
  const cfopsFora   = cfops.filter(c => form.tipo_operacao === 'S' ? c.codigo[0]==='6' && !['6401','6402','6403','6404'].includes(c.codigo) : c.codigo[0]==='2');
  const stCodsDentro = form.tipo_operacao==='S' ? ['5401','5402','5403','5405'] : ['1401','1402','1403','1405'];
  const stCodsFora   = form.tipo_operacao==='S' ? ['6401','6402','6403','6404'] : ['2401','2402','2403','2404'];
  const prefD = form.tipo_operacao==='S' ? '5' : '1';
  const prefF = form.tipo_operacao==='S' ? '6' : '2';
  const cfopsStDentro = [...cfops.filter(c=>stCodsDentro.includes(c.codigo)), ...cfops.filter(c=>c.codigo[0]===prefD && !stCodsDentro.includes(c.codigo))];
  const cfopsStFora   = [...cfops.filter(c=>stCodsFora.includes(c.codigo)),   ...cfops.filter(c=>c.codigo[0]===prefF && !stCodsFora.includes(c.codigo))];
  const cfopsSaida = cfops.filter(c => c.codigo[0]==='5' || c.codigo[0]==='6');
  const cfopsEntrada = cfops.filter(c => c.codigo[0]==='1' || c.codigo[0]==='2');

  const tipoLabel = (t) => t === 'S' ? 'Saída' : 'Entrada';
  const ehEntrada = form.tipo_operacao === 'E';
  const podeMostrarConversoes = editId && ehEntrada;

  return (
    <div>
      {/* Topbar */}
      <div className={s.topbar}>
        <h2 className={s.titulo}>Tipos de Movimento Fiscal</h2>
        <div className={s.acoes}>
          {!mostrarForm && <button className={s.btnPrimario} onClick={novo}>+ Novo</button>}
          {mostrarForm  && <button className={s.btnSecundario} onClick={fecharForm}>Fechar</button>}
        </div>
      </div>

      {/* ── Formulário (colapsável) ── */}
      {mostrarForm && (
        <div className={sf.formWrap}>
          <div className={sf.formHeader}>
            {editId ? `Editando: ${form.nome || '…'}` : 'Novo Tipo de Movimento Fiscal'}
          </div>
          <form onSubmit={salvar} className={sf.formGrid} data-enter-scope="on" onKeyDown={navegarComEnter}>

            {/* Nome + Tipo op */}
            <div className={sf.formRow}>
              <label className={sf.label} style={{ flex: 2 }}>
                Nome *
                <input className={sf.input} value={form.nome} required
                  placeholder="Ex: Compra para Revenda"
                  onChange={e => set('nome', e.target.value)} />
              </label>
              <label className={sf.label} style={{ width: 180 }}>
                Tipo de Operação *
                <select className={sf.select} value={form.tipo_operacao}
                  onChange={e => { set('tipo_operacao', e.target.value); set('cfop_dentro_estado_id',''); set('cfop_fora_estado_id',''); }}>
                  {TIPO_OP.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}
                </select>
              </label>
            </div>

            {/* CFOPs principais */}
            <div className={sf.formRow}>
              <label className={sf.label} style={{ flex: 1 }}>
                CFOP Dentro do Estado * <span className={sf.hint}>({ehEntrada ? '1xxx' : '5xxx'})</span>
                <select className={sf.select} value={form.cfop_dentro_estado_id}
                  onChange={e => {
                    const codigo = e.target.value;
                    setForm(f => {
                      const descricao = cfopsDentro.find(c => c.codigo === codigo)?.descricao || '';
                      const natureza = (!f.natureza_operacao && descricao) ? descricao.toUpperCase() : f.natureza_operacao;
                      return { ...f, cfop_dentro_estado_id: codigo, natureza_operacao: natureza };
                    });
                  }}>
                  <option value="">— selecione —</option>
                  {cfopsDentro.map(c => <option key={c.codigo} value={c.codigo}>{c.codigo} — {c.descricao}</option>)}
                </select>
              </label>
              <label className={sf.label} style={{ flex: 1 }}>
                CFOP Fora do Estado * <span className={sf.hint}>({ehEntrada ? '2xxx' : '6xxx'})</span>
                <select className={sf.select} value={form.cfop_fora_estado_id}
                  onChange={e => set('cfop_fora_estado_id', e.target.value)}>
                  <option value="">— selecione —</option>
                  {cfopsFora.map(c => <option key={c.codigo} value={c.codigo}>{c.codigo} — {c.descricao}</option>)}
                </select>
              </label>
            </div>

            {/* Natureza da Operação */}
            <div className={sf.formRow}>
              <label className={sf.label} style={{ flex: 1 }}>
                Natureza da Operação <span className={sf.hint}>(vai no cabeçalho da NF-e/NFC-e)</span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input className={sf.input} value={form.natureza_operacao} maxLength={120}
                    placeholder="Ex: VENDA DE MERCADORIA"
                    onChange={e => set('natureza_operacao', e.target.value.toUpperCase())} />
                  <button type="button" className={s.btnSecundario}
                    title="Usar a descrição do CFOP Dentro do Estado"
                    disabled={!form.cfop_dentro_estado_id}
                    onClick={() => {
                      const descricao = cfopsDentro.find(c => c.codigo === form.cfop_dentro_estado_id)?.descricao;
                      if (descricao) set('natureza_operacao', descricao.toUpperCase());
                    }}>
                    🔄 usar descrição do CFOP
                  </button>
                </div>
              </label>
            </div>

            {/* CFOPs ST */}
            <div className={sf.formRow}>
              <div className={sf.stSection} style={{ flex: 1 }}>
                <div className={sf.stHeader}>ST — Substituição Tributária</div>
                <div className={sf.formRow} style={{ marginTop: 8 }}>
                  <label className={sf.label} style={{ flex: 1 }}>
                    CFOP ST Dentro
                    <input className={sf.input} list="cfop-st-dentro-list"
                      value={form.cfop_st_dentro_estado_id} maxLength={4}
                      placeholder={ehEntrada ? '1403' : '5403'}
                      onChange={e => set('cfop_st_dentro_estado_id', e.target.value.trim())} />
                    <datalist id="cfop-st-dentro-list">
                      {cfopsStDentro.map(c => <option key={c.codigo} value={c.codigo}>{c.codigo} — {c.descricao}</option>)}
                    </datalist>
                  </label>
                  <label className={sf.label} style={{ flex: 1 }}>
                    CFOP ST Fora
                    <input className={sf.input} list="cfop-st-fora-list"
                      value={form.cfop_st_fora_estado_id} maxLength={4}
                      placeholder={ehEntrada ? '2403' : '6403'}
                      onChange={e => set('cfop_st_fora_estado_id', e.target.value.trim())} />
                    <datalist id="cfop-st-fora-list">
                      {cfopsStFora.map(c => <option key={c.codigo} value={c.codigo}>{c.codigo} — {c.descricao}</option>)}
                    </datalist>
                  </label>
                </div>
              </div>

              {/* CFOPs Produção Própria */}
              <div className={sf.stSection} style={{ flex: 1 }}>
                <div className={sf.stHeader}>Produção Própria</div>
                <div className={sf.formRow} style={{ marginTop: 8 }}>
                  <label className={sf.label} style={{ flex: 1 }}>
                    CFOP Produção Dentro
                    <input className={sf.input} list="cfop-producao-dentro-list"
                      value={form.cfop_producao_dentro_estado_id} maxLength={4}
                      placeholder={ehEntrada ? '1101' : '5101'}
                      onChange={e => set('cfop_producao_dentro_estado_id', e.target.value.trim())} />
                    <datalist id="cfop-producao-dentro-list">
                      {cfopsDentro.map(c => <option key={c.codigo} value={c.codigo}>{c.codigo} — {c.descricao}</option>)}
                    </datalist>
                  </label>
                  <label className={sf.label} style={{ flex: 1 }}>
                    CFOP Produção Fora
                    <input className={sf.input} list="cfop-producao-fora-list"
                      value={form.cfop_producao_fora_estado_id} maxLength={4}
                      placeholder={ehEntrada ? '2101' : '6101'}
                      onChange={e => set('cfop_producao_fora_estado_id', e.target.value.trim())} />
                    <datalist id="cfop-producao-fora-list">
                      {cfopsFora.map(c => <option key={c.codigo} value={c.codigo}>{c.codigo} — {c.descricao}</option>)}
                    </datalist>
                  </label>
                </div>
                <div className={sf.hint} style={{ marginTop: 4 }}>
                  Usado quando o produto tem "Produção Própria" marcada no cadastro.
                </div>
              </div>

              {/* Comportamentos */}
              <div className={sf.checkSection} style={{ width: 200, alignSelf: 'flex-start' }}>
                <label className={sf.checkLabel}>
                  <input type="checkbox" checked={form.gera_financeiro} onChange={e => set('gera_financeiro', e.target.checked)} />
                  Gera financeiro
                </label>
                <label className={sf.checkLabel}>
                  <input type="checkbox" checked={form.movimenta_estoque} onChange={e => set('movimenta_estoque', e.target.checked)} />
                  Movimenta estoque
                </label>
                <label className={sf.checkLabel}>
                  <input type="checkbox" checked={form.usa_preco_custo} onChange={e => set('usa_preco_custo', e.target.checked)} />
                  Usar preço de custo
                </label>
                <label className={sf.checkLabel} title="Quando marcado, este tipo aparece no seletor da tela de Pedidos/Orçamentos">
                  <input type="checkbox" checked={form.mostrar_em_pedidos} onChange={e => set('mostrar_em_pedidos', e.target.checked)} />
                  Mostrar em Pedidos/Orçamentos
                </label>
                <label className={sf.checkLabel} title="Com a rotina 'Validar estoque insuficiente' ativa (Parâmetros → Rotinas), vendas com este tipo de movimento pedem senha admin/gerente se deixarem o estoque negativo. Sem esta marcação, este tipo não é validado.">
                  <input type="checkbox" checked={form.verificar_estoque_insuficiente} onChange={e => set('verificar_estoque_insuficiente', e.target.checked)} />
                  Verificar estoque insuficiente
                </label>
              </div>
            </div>

            {/* Setor + Obs */}
            <div className={sf.formRow}>
              {form.movimenta_estoque && (
                <label className={sf.label} style={{ flex: 1 }}>
                  Setor Padrão de {ehEntrada ? 'Entrada' : 'Saída'}
                  <select className={sf.select} value={form.setor_estoque_id}
                    onChange={e => set('setor_estoque_id', e.target.value)}>
                    <option value="">— nenhum / padrão da organização —</option>
                    {setores.map(st => <option key={st.id} value={st.id}>{st.sigla} — {st.nome}</option>)}
                  </select>
                </label>
              )}
              <label className={sf.label} style={{ flex: 1 }}>
                Observação
                <textarea className={sf.textarea} rows={2} value={form.observacao}
                  placeholder="Observações sobre o uso deste tipo de movimento..."
                  onChange={e => set('observacao', e.target.value)} />
              </label>
            </div>

            {/* ── Índices para Coeficiente de Preço (Simulação de Preço) ── */}
            <div style={{ border: '1px solid #93c5fd', borderRadius: 6, padding: '10px 12px', marginTop: 4 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: '#1e3a5f', marginBottom: 2 }}>
                Índices para Coeficiente de Preço e Compras
              </div>
              <div style={{ fontSize: 11, color: '#667', marginBottom: 8 }}>
                Usados na Simulação de Preço ({orgData?.simulacao_preco === 'markup_comum'
                  ? 'markup comum — % sobre o custo' : 'markup divisor — % sobre a venda'}).
                Regime da empresa: <strong>{orgData?.regime_tributario || 'Simples Nacional'}</strong>
                {' '}— {ehSimples
                  ? 'vale o %Simples Nac (ICMS/CSLL/IRPJ não se aplicam)'
                  : 'vale o ICMS efetivo do estado + CSLL/IRPJ (%Simples não se aplica)'}.
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                {IDX_CAMPOS.map(c => {
                  const aplicavel = c.regime === 'ambos' || (ehSimples ? c.regime === 'simples' : c.regime === 'normal');
                  const destaque  = (ehSimples && c.k === 'idx_simples_pct') || (!ehSimples && c.k === 'idx_icms_pct');
                  return (
                    <label key={c.k} style={{ display: 'flex', flexDirection: 'column', gap: 3,
                      fontSize: 11.5, fontWeight: destaque ? 700 : 500,
                      color: destaque ? '#166534' : aplicavel ? '#334155' : '#9aa3af', width: 92 }}
                      title={aplicavel ? '' : `Não se aplica ao regime ${orgData?.regime_tributario || ''}`}>
                      {c.label}
                      <input type="number" min="0" max="100" step="0.001" className={sf.input}
                        value={form[c.k]}
                        disabled={!aplicavel}
                        style={destaque
                          ? { borderColor: '#16a34a', background: '#f0fdf4', fontWeight: 700 }
                          : !aplicavel ? { background: '#f1f5f9', color: '#9aa3af' } : undefined}
                        onChange={e => set(c.k, e.target.value)} />
                    </label>
                  );
                })}
                <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11.5,
                  fontWeight: 700, color: '#b91c1c', width: 100 }}>
                  %Total Imposto
                  <input readOnly tabIndex={-1} className={sf.input}
                    style={{ background: '#fef2f2', color: '#b91c1c', fontWeight: 700 }}
                    value={IDX_CAMPOS
                      .filter(c => c.regime === 'ambos' || (ehSimples ? c.regime === 'simples' : c.regime === 'normal'))
                      .reduce((s, c) => s + (parseFloat(form[c.k]) || 0), 0)
                      .toFixed(2).replace('.', ',')} />
                </label>
              </div>
            </div>

            {erro && <p className={sf.erro}>{erro}</p>}
            {ok   && <p className={sf.ok}>{ok}</p>}

            <div className={sf.formAcoes}>
              <button type="button" className={s.btnSecundario} onClick={fecharForm}>Fechar</button>
              <button type="submit" className={s.btnPrimario} data-enter-final disabled={loading}>
                {loading ? 'Salvando…' : editId ? 'Atualizar' : 'Salvar e Continuar'}
              </button>
            </div>
          </form>

          {/* ── Conversões (só para entrada, só quando já salvo) ── */}
          {podeMostrarConversoes && (
            <div className={sf.convRow}>
              <TabelaCfopConversao
                itens={convCfop}
                onAdd={addConvCfop}
                onRemove={remConvCfop}
                cfopsXml={cfopsSaida}
                cfopsEntrada={cfopsEntrada}
              />
              <TabelaConversao
                titulo="Conversão de CST/CSOSN (ICMS)"
                hint="CST/CSOSN do XML → CST para Entrada e Saída no produto"
                colunaXml="CST XML" colunaEntrada="Entrada" colunaSaida="Saída"
                itens={convCstIcms.map(c => ({ cst_xml: c.cst_xml, cst_entrada: c.cst_entrada, cst_saida: c.cst_saida }))}
                onAdd={addConvCstIcms}
                onRemove={remConvCstIcms}
                optsXml={CSTS_ICMS}
                optsEntrada={CSTS_ICMS}
                optsSaida={CSTS_ICMS}
              />
              <TabelaConversao
                titulo="Conversão de CST PIS/COFINS"
                hint="CST PIS/COFINS do XML → CST para Entrada e Saída no produto"
                colunaXml="CST XML" colunaEntrada="Entrada" colunaSaida="Saída"
                itens={convPisCofins.map(c => ({ cst_xml: c.cst_xml, cst_entrada: c.cst_entrada, cst_saida: c.cst_saida }))}
                onAdd={addConvPisCofins}
                onRemove={remConvPisCofins}
                optsXml={CSTS_PIS_COFINS}
                optsEntrada={CSTS_PIS_COFINS}
                optsSaida={CSTS_PIS_COFINS}
              />
            </div>
          )}
          {editId && !ehEntrada && (
            <>
              <div style={{ padding: '8px 16px', fontSize: '0.78rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                Conversões de CST/CFOP são configuradas apenas para tipos de Entrada.
              </div>
              {/* ── Config. Fiscal "Fora do Estado" — regime normal, só Saída
                  (2026-09-16). Sobrescreve ICMS/PIS/COFINS do CADASTRO DO
                  PRODUTO quando a venda deste tipo de movimento vai pra fora
                  do estado — Simples Nacional (CSOSN) não usa isso, e dentro
                  do estado o produto continua mandando como sempre. Cada
                  bloco (ICMS/PIS/COFINS) só entra em vigor se o respectivo
                  "Sit. trib." estiver preenchido — em branco, comportamento
                  IDÊNTICO a hoje. ── */}
              <div style={{ border: '1px solid #fbbf24', borderRadius: 6, padding: '10px 12px', margin: '8px 16px' }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: '#92400e', marginBottom: 2 }}>
                  Config. Fiscal — Fora do Estado (regime normal)
                </div>
                <div style={{ fontSize: 11, color: '#667', marginBottom: 8 }}>
                  Sobrescreve o ICMS/PIS/COFINS do cadastro do produto só quando a venda
                  deste tipo de movimento vai pra fora do estado do emitente. Deixe um
                  bloco (ICMS, PIS ou COFINS) todo em branco pra continuar usando o que
                  está no cadastro do produto, igual sempre foi — não se aplica pra
                  Simples Nacional.
                </div>

                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 8 }}>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11.5, width: 160 }}>
                    ICMS — Sit. trib.
                    <select className={sf.select} value={formForaEstado.icms_cst}
                      onChange={e => setForaEstado('icms_cst', e.target.value)}>
                      <option value="">— não sobrescreve —</option>
                      {CSTS_ICMS.filter(c => c.length === 2).map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11.5, width: 150 }}>
                    Mod. BC
                    <select className={sf.select} value={formForaEstado.icms_mod_bc}
                      onChange={e => setForaEstado('icms_mod_bc', e.target.value)}>
                      <option value="">—</option>
                      {MODS_BC_ICMS.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
                    </select>
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11.5, width: 90 }}>
                    Red. BC %
                    <input type="number" min="0" max="100" step="0.01" className={sf.input}
                      value={formForaEstado.icms_red_bc_perc}
                      onChange={e => setForaEstado('icms_red_bc_perc', e.target.value)} />
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11.5, width: 90 }}>
                    Alíq. %
                    <input type="number" min="0" max="100" step="0.01" className={sf.input}
                      value={formForaEstado.icms_aliq_perc}
                      onChange={e => setForaEstado('icms_aliq_perc', e.target.value)} />
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11.5, width: 90 }}>
                    Dif. %
                    <input type="number" min="0" max="100" step="0.01" className={sf.input}
                      value={formForaEstado.icms_dif_perc}
                      onChange={e => setForaEstado('icms_dif_perc', e.target.value)} />
                  </label>
                </div>

                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 8 }}>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11.5, width: 150 }}>
                    ICMS-ST — Mod. BC
                    <select className={sf.select} value={formForaEstado.icms_st_mod_bc}
                      onChange={e => setForaEstado('icms_st_mod_bc', e.target.value)}>
                      <option value="">—</option>
                      {MODS_BC_ICMS.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
                    </select>
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11.5, width: 90 }}>
                    Red. BC ST %
                    <input type="number" min="0" max="100" step="0.01" className={sf.input}
                      value={formForaEstado.icms_st_red_bc_perc}
                      onChange={e => setForaEstado('icms_st_red_bc_perc', e.target.value)} />
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11.5, width: 80 }}>
                    MVA %
                    <input type="number" min="0" max="100" step="0.01" className={sf.input}
                      value={formForaEstado.icms_st_mva_perc}
                      onChange={e => setForaEstado('icms_st_mva_perc', e.target.value)} />
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11.5, width: 90 }}>
                    Alíq. ST %
                    <input type="number" min="0" max="100" step="0.01" className={sf.input}
                      value={formForaEstado.icms_st_aliq_perc}
                      onChange={e => setForaEstado('icms_st_aliq_perc', e.target.value)} />
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11.5, width: 100 }}>
                    BC Ret. R$
                    <input type="number" min="0" step="0.01" className={sf.input}
                      value={formForaEstado.icms_st_ret_bc}
                      onChange={e => setForaEstado('icms_st_ret_bc', e.target.value)} />
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11.5, width: 100 }}>
                    Valor Ret. R$
                    <input type="number" min="0" step="0.01" className={sf.input}
                      value={formForaEstado.icms_st_ret_valor}
                      onChange={e => setForaEstado('icms_st_ret_valor', e.target.value)} />
                  </label>
                </div>

                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 8 }}>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11.5, width: 120 }}>
                    PIS — Sit. trib.
                    <select className={sf.select} value={formForaEstado.pis_cst}
                      onChange={e => setForaEstado('pis_cst', e.target.value)}>
                      <option value="">— não sobrescreve —</option>
                      {CSTS_PIS_COFINS.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11.5, width: 90 }}>
                    Alíq. %
                    <input type="number" min="0" max="100" step="0.01" className={sf.input}
                      value={formForaEstado.pis_aliq_perc}
                      onChange={e => setForaEstado('pis_aliq_perc', e.target.value)} />
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11.5, width: 120 }}>
                    COFINS — Sit. trib.
                    <select className={sf.select} value={formForaEstado.cofins_cst}
                      onChange={e => setForaEstado('cofins_cst', e.target.value)}>
                      <option value="">— não sobrescreve —</option>
                      {CSTS_PIS_COFINS.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11.5, width: 90 }}>
                    Alíq. %
                    <input type="number" min="0" max="100" step="0.01" className={sf.input}
                      value={formForaEstado.cofins_aliq_perc}
                      onChange={e => setForaEstado('cofins_aliq_perc', e.target.value)} />
                  </label>
                </div>

                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <button type="button" className={s.btnPrimario} disabled={salvandoForaEstado} onClick={salvarForaEstado}>
                    {salvandoForaEstado ? 'Salvando...' : 'Salvar Config. Fora do Estado'}
                  </button>
                  {erroForaEstado && <span style={{ color: '#dc2626', fontSize: 12 }}>{erroForaEstado}</span>}
                  {okForaEstado   && <span style={{ color: '#16a34a', fontSize: 12 }}>{okForaEstado}</span>}
                </div>
              </div>
            </>
          )}
          {!editId && ehEntrada && (
            <div style={{ padding: '8px 16px', fontSize: '0.78rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
              Salve para habilitar a configuração de conversões.
            </div>
          )}
        </div>
      )}

      {/* ── Lista ── */}
      <div className={sf.tableWrap} style={{ marginTop: mostrarForm ? 16 : 0 }}>
        <table className={sf.table}>
          <thead>
            <tr>
              <th>Nome</th>
              <th style={{ width: 70 }}>Op.</th>
              <th style={{ width: 60 }}>CFOP D</th>
              <th style={{ width: 60 }}>CFOP F</th>
              <th style={{ width: 60 }}>ST D</th>
              <th style={{ width: 60 }}>ST F</th>
              <th style={{ width: 60 }}>Prod. D</th>
              <th style={{ width: 60 }}>Prod. F</th>
              <th style={{ width: 36, textAlign: 'center' }}>Fin.</th>
              <th style={{ width: 36, textAlign: 'center' }}>Est.</th>
              <th style={{ width: 36, textAlign: 'center' }}>Conv.</th>
              <th style={{ width: 120 }}></th>
            </tr>
          </thead>
          <tbody>
            {lista.length === 0 && (
              <tr><td colSpan={12} className={sf.vazio}>Nenhum tipo cadastrado.</td></tr>
            )}
            {lista.map(item => (
              <tr key={item.id} className={editId === item.id ? sf.rowAtiva : ''}>
                <td>
                  <strong className={sf.nome}>{item.nome}</strong>
                  {item.observacao && <div className={sf.itemObs}>{item.observacao}</div>}
                </td>
                <td>
                  <span className={item.tipo_operacao==='S' ? sf.badgeSaida : sf.badgeEntrada}>
                    {tipoLabel(item.tipo_operacao)}
                  </span>
                </td>
                <td>{item.cfop_dentro_estado_id ? <span className={sf.cfop}>{item.cfop_dentro_estado_id}</span> : <span className={sf.sem}>—</span>}</td>
                <td>{item.cfop_fora_estado_id   ? <span className={sf.cfop}>{item.cfop_fora_estado_id}</span>   : <span className={sf.sem}>—</span>}</td>
                <td>{item.cfop_st_dentro_estado_id ? <span className={sf.cfopSt}>{item.cfop_st_dentro_estado_id}</span> : <span className={sf.sem}>—</span>}</td>
                <td>{item.cfop_st_fora_estado_id   ? <span className={sf.cfopSt}>{item.cfop_st_fora_estado_id}</span>   : <span className={sf.sem}>—</span>}</td>
                <td>{item.cfop_producao_dentro_estado_id ? <span className={sf.cfopSt}>{item.cfop_producao_dentro_estado_id}</span> : <span className={sf.sem}>—</span>}</td>
                <td>{item.cfop_producao_fora_estado_id   ? <span className={sf.cfopSt}>{item.cfop_producao_fora_estado_id}</span>   : <span className={sf.sem}>—</span>}</td>
                <td style={{ textAlign: 'center' }}>
                  <span className={item.gera_financeiro ? sf.checkSim : sf.checkNao}>{item.gera_financeiro ? '✔' : '✘'}</span>
                </td>
                <td style={{ textAlign: 'center' }}>
                  <span className={item.movimenta_estoque ? sf.checkSim : sf.checkNao}>{item.movimenta_estoque ? '✔' : '✘'}</span>
                </td>
                <td style={{ textAlign: 'center' }}>
                  {item.tipo_operacao === 'E'
                    ? <span className={sf.checkSim} title="Conversões configuráveis">✔</span>
                    : <span className={sf.sem}>—</span>}
                </td>
                <td className={sf.acoes}>
                  <button className={sf.btnEdit} onClick={() => editar(item)}>Editar</button>
                  <button className={s.btnPerigo} onClick={() => excluir(item)}>Excluir</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
